import { DateTime } from 'luxon';
import { fetchQuery, graphql } from 'react-relay';

import { TradeListLoadingActions } from 'src/components/Trade/TradeList/reducer/constants';
import { APIConfig } from 'src/config';
import { TRADE_LIST_TRADE_RULE_QUERY_PAGE_SIZE } from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import logger from 'src/util/logger';
import buildTradeListCursor from 'src/util/trades';

/**
 * Graphql query for retrieving meters of a property.
 */
// First stage: Load all meters for the property.
const PropertyMetersQuery = graphql`
  query dataBatchingPropertyMetersQuery(
    $propertyId: ID!,
    $after: String,
  ) {
    property(uuid: $propertyId) {
      uuid
      meters(first: 500, after: $after) {
        edges {
          node {
            uuid
            identifier
            title
            tradePointId
          }
        }
      }
    }
  }
`;

/**
 * Stage 1: Fetch meters for the property provided.
 * @param {object} args - Args for fetching meters
 * @param {string} args.propertyId - Property ID
 * @param {object} args.environment - Relay environment
 * @returns {Promise<Array>} Promise that resolves to array of meter objects
 */
export const getPropertyMeters = async (args) => {
  const { propertyId, environment } = args;

  const variables = { propertyId };

  logger.log(`Fetching meters for property ${propertyId}`);

  return queryWithRetry({
    queryFn: () => {
      let resolvePromise;
      let rejectPromise;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const subscription = fetchQuery(environment, PropertyMetersQuery, variables).subscribe({
        next: (data) => {
          if (!data?.property?.meters?.edges) {
            logger.warn(`No meters returned for property ${propertyId}`);
            resolvePromise([]);
            return;
          }

          const meters = data.property.meters.edges.map((edge) => edge.node);
          logger.log(`${meters.length} meters returned for property ${propertyId}`);
          resolvePromise(meters);
        },
        error: (error) => {
          rejectPromise(error);
        },
      });

      // Attach subscription methods to the promise so it can be cancelled
      promise.unsubscribe = () => subscription.unsubscribe();

      return promise;
    },
    timeoutMs: APIConfig().GRAPH_API_TIMEOUT || 30000,
    operationName: `Fetching meters for property ${propertyId}`,
    maxRetries: 3,
  });
};

/**
 * Graphql query for retrieving trade rules for a property.
 */

// Load trade rules for a single meter within time range.
// Rules are paginated using the standard Relay connection pattern.
// We iterate using pageInfo.hasNextPage and pageInfo.endCursor until all
// rules have been fetched for a meter.
const MeterTradeRulesQuery = graphql`
  query dataBatchingMeterTradeRulesQuery(
    $meterId: ID!,
    $start: Timestamp!,
    $finish: Timestamp!,
    $after: String
    $rulesPageSize: Int!
  ) {
    meter(uuid: $meterId) {
      uuid
      rules(first: $rulesPageSize, start: $start, finish: $finish, after: $after) {
        edges {
          node {
            uuid
            buyer {
              tradePoint {
                uuid
                type
                meter {
                 uuid
                 identifier
                 title
                 property {
                  uuid
                  title
                 }
                }
              }
              userId
              user {
                uuid
                email
                givenName
                familyName
              }
              communityId
              residualId
            }
            seller {
              userId
              user {
                email
                givenName
                familyName
              }
              communityId
              residualId
              tradePoint {
                uuid
                type
                meter {
                  uuid
                  identifier
                  title
                  property {
                    uuid
                    title
                  }
                }
              }
            }
          }
        }
        pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      }
    }
  }
`;

/**
 * Retrieval of a single page of trade rules for a meter within a time range.
 * Used by getAllRulesForMeter to iterate over the rules connection.
 * @param {object} args - Args for fetching a page of rules
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - Batch start time (Luxon DateTime)
 * @param {object} args.finish - Batch finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} [args.after] - After cursor for the rules connection
 * @returns {Promise<object>} Promise that resolves to meter with a page of rules data
 */
export const getRulesForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    after = null,
  } = args;

  const variables = {
    meterId,
    start: Math.floor(start.toSeconds()),
    finish: Math.floor(finish.toSeconds()),
    rulesPageSize: TRADE_LIST_TRADE_RULE_QUERY_PAGE_SIZE,
    after,
  };

  logger.log(`Fetching rules for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`);

  return queryWithRetry({
    queryFn: () => {
      let resolvePromise;
      let rejectPromise;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const subscription = fetchQuery(environment, MeterTradeRulesQuery, variables).subscribe({
        next: (data) => {
          if (!data?.meter) {
            logger.warn(`Failed to rules for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after} as meter does not exist`);
            resolvePromise({ uuid: meterId, rules: { edges: [] } });
            return;
          }

          const rulesCount = data.meter.rules?.edges?.length || 0;
          logger.log(`Fetched ${rulesCount} rules for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`);
          resolvePromise(data.meter);
        },
        error: (error) => {
          rejectPromise(error);
        },
      });

      // Attach subscription methods to the promise so it can be cancelled
      promise.unsubscribe = () => subscription.unsubscribe();

      return promise;
    },
    timeoutMs: 60000,
    operationName: `Fetching rules for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`,
    maxRetries: 3,
  });
};

/**
 * Fetches ALL trade rules for a meter by continuously fetching pages until no
 * more data is available. Uses the standard Relay pageInfo.hasNextPage and
 * pageInfo.endCursor fields on the rules connection to determine when to stop.
 * @param {object} args - Args for fetching all rules
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - Batch start time (Luxon DateTime)
 * @param {object} args.finish - Batch finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {Function} args.stateDispatchFunc - Function to dispatch state changes
 * @returns {Promise<object>} An object containing the rules for the meter.
 */
export const getAllRulesForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    stateDispatchFunc,
  } = args;

  logger.log(`Fetching ALL rules for meter ${meterId} from ${start.toISO()} to ${finish.toISO()}`);

  const allRules = [];
  let after = null;
  let hasMoreData = true;
  let pageCount = 0;

  while (hasMoreData) {
    try {
      // sequential behavior is intentional
      // each API call depends on the cursor from the previous response
      // eslint-disable-next-line no-await-in-loop
      const meterData = await getRulesForMeter({
        meterId,
        start,
        finish,
        environment,
        after,
      });

      const rules = meterData?.rules?.edges || [];
      const pageInfo = meterData?.rules?.pageInfo || {};

      if (rules.length === 0) {
        // No rules returned, we're done
        hasMoreData = false;
        logger.log(`No more rules found for meter ${meterId} after ${pageCount} pages`);
      } else {
        // Add rules to our collection
        allRules.push(...rules);
        pageCount += 1;
        if (stateDispatchFunc) {
          stateDispatchFunc({
            type: TradeListLoadingActions.RULES_LOADED,
            addedRulesCount: rules.length,
            complete: false,
          });
        }

        if (pageInfo.hasNextPage && pageInfo.endCursor) {
          // Continue with next page using the Relay cursor
          after = pageInfo.endCursor;
          logger.info(`Fetched rules page ${pageCount} for meter ${meterId}: ${rules.length} rules, continuing with cursor ${after}`);
        } else {
          // No more pages according to Relay pageInfo
          hasMoreData = false;
          logger.info(`Reached end of rules for meter ${meterId} after ${pageCount} pages`);
        }
      }
    } catch (error) {
      logger.error(`Error fetching rules page ${pageCount + 1} for meter ${meterId}:`, error);
      // Re-throw the error to propagate it upward and fail fast
      // This ensures incomplete data is not silently returned when fetching fails
      throw error;
    }
  }

  const totalRules = allRules.length;
  logger.info(`Completed fetching ALL rules for meter ${meterId}: ${totalRules} total rules across ${pageCount} pages`);

  return {
    uuid: meterId,
    rules: {
      edges: allRules,
    },
  };
};

/**
 * Graphql query to load trades for a single meter within time range.
 */
const MeterTradesPaginatedQuery = graphql`
  query dataBatchingTradesPaginatedQuery(
    $meterId: ID!,
    $start: Timestamp!,
    $finish: Timestamp!,
    $pageSize: Int!,
    $after: String
  ) {
    meter(uuid: $meterId) {
      uuid
      tradePointId
      trades(start: $start, finish: $finish, pageSize: $pageSize, after: $after) {
        edges {
          node {
            uuid
            ruleId
            buyerTradePointId
            sellerTradePointId
            price
            value
            volume
            direction
            interval {
              timestamp
              length
            }
            type
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

/**
 * Retrieve a single page of trades for a meter covering a period of time.
 * @param {object} args - Args for fetching trades
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - Batch start time (Luxon DateTime)
 * @param {object} args.finish - Batch finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {number} args.pageSize - Page size for trades
 * @param {string} [args.after] - After cursor point for page
 * @returns {Promise<object>} Promise that resolves to meter with trades data.
 */
export const getTradesForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    pageSize,
    after = null,
  } = args;

  const variables = {
    meterId,
    start: Math.floor(start.toSeconds()),
    finish: Math.floor(finish.toSeconds()),
    pageSize,
    after,
  };

  logger.log(`Fetching trades for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`);

  return queryWithRetry({
    queryFn: () => {
      let resolvePromise;
      let rejectPromise;

      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const subscription = fetchQuery(environment, MeterTradesPaginatedQuery, variables).subscribe({
        next: (data) => {
          if (!data?.meter) {
            logger.warn(`No meter returned for ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`);
            resolvePromise({ uuid: meterId, trades: { edges: [], pageInfo: {} } });
            return;
          }

          const tradesCount = data.meter.trades?.edges?.length || 0;
          logger.log(`Fetched ${tradesCount} trades for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`);
          resolvePromise(data.meter);
        },
        error: (error) => {
          rejectPromise(error);
        },
      });

      // Attach subscription methods to the promise so it can be cancelled
      promise.unsubscribe = () => subscription.unsubscribe();

      return promise;
    },
    timeoutMs: APIConfig().GRAPH_API_TIMEOUT || 30000,
    operationName: `Fetching trades for meter ${meterId} from ${start.toISO()} to ${finish.toISO()} after ${after}`,
    maxRetries: 3,
  });
};

/**
 * Retrieves all trades for a meter by continuously fetching pages until no more data is available.
 * Uses an alternative mechanism to determine completion since hasNextPage is broken on the backend.
 * See `tradeListCursor` in src/util/trades for the helper used for the after cursor.
 * @param {object} args - Args for fetching all trades
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - Batch start time (Luxon DateTime)
 * @param {object} args.finish - Batch finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {number} args.pageSize - Page size for trades
 * @param {Function} args.stateDispatchFunc - Function to dispatch state changes
 * @returns {Promise<object>} Promise that resolves to meter with all trades data.
 */
export const getAllTradesForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    pageSize,
    stateDispatchFunc,
  } = args;

  logger.info(`Fetching ALL trades for meter ${meterId} from ${start.toISO()} to ${finish.toISO()}`);

  const allTrades = [];
  let after = null;
  let hasMoreData = true;
  let pageCount = 0;

  while (hasMoreData) {
    try {
      // sequential behavior is intentional
      // each API call depends on the cursor from the previous response
      // eslint-disable-next-line no-await-in-loop
      const meterData = await getTradesForMeter({
        meterId,
        start,
        finish,
        environment,
        pageSize,
        after,
      });

      const tradePointId = meterData?.tradePointId;
      const trades = meterData?.trades?.edges || [];
      const pageInfo = meterData?.trades?.pageInfo || {};

      if (trades.length === 0) {
        // No trades returned, we're done
        hasMoreData = false;
        logger.log(`No more trades found for meter ${meterId} after ${pageCount} pages`);
      } else {
        // Add trades to our collection
        allTrades.push(...trades);
        pageCount += 1;
        if (stateDispatchFunc) {
          stateDispatchFunc({
            type: TradeListLoadingActions.TRADES_LOADED,
            addedTradesCount: trades.length,
            complete: false,
          });
        }

        // Check if we should continue fetching
        // Since hasNextPage is broken, we use alternative indicators:
        // 1. If we got fewer trades than requested, we're likely at the end
        // 2. If we have no endCursor, we're at the end
        if (trades.length < pageSize || !pageInfo.endCursor) {
          hasMoreData = false;
          logger.info(`Reached end of trades for meter ${meterId} after ${pageCount} pages (got ${trades.length} trades, expected ${pageSize})`);
        } else {
          // Continue with next page
          const lastTrade = trades[trades.length - 1].node;
          after = buildTradeListCursor(tradePointId, lastTrade.uuid, lastTrade.interval.timestamp);
          logger.log(`Fetched page ${pageCount} for meter ${meterId}: ${trades.length} trades, continuing with cursor ${after}`);
        }
      }
    } catch (error) {
      logger.error(`Fetching trades page ${pageCount + 1} for meter ${meterId}:`, error);
      // Re-throw the error to propagate it upward and fail fast
      // This ensures incomplete data is not silently returned when fetching fails
      throw error;
    }
  }

  const totalTrades = allTrades.length;
  logger.info(`Completed fetching ALL trades for meter ${meterId}: ${totalTrades} total trades across ${pageCount} pages`);

  // Return the meter data with all trades combined
  return {
    uuid: meterId,
    trades: {
      edges: allTrades,
    },
  };
};

/**
 * Builds the resulting data structure of trade rules and trades for the meters.
 * @param {Array} meters - Array of meter objects from stage 1
 * @param {Array} ruleData - Array of meter objects with rules from stage 2
 * @param {Array} tradeData - Array of meter objects with trades from stage 3
 * @returns {object} Merged data in the expected format with pagination info
 */
export const buildPropertyTradedData = (meters, ruleData, tradeData) => {
  logger.log(`Aggregate meter information, trade rules and trade data: ${meters.length} meters, ${ruleData.length} rules datasets, ${tradeData.length} trades datasets`);

  const rulesByMeterId = new Map(
    ruleData
      .filter((meterRules) => meterRules.uuid)
      .map((meterRules) => [meterRules.uuid, meterRules.rules]),
  );

  const tradesByMeterId = new Map(
    tradeData
      .filter((meterTrades) => meterTrades.uuid)
      .map((meterTrades) => [meterTrades.uuid, meterTrades.trades]),
  );

  // Merge data for each meter
  const mergedMeterEdges = meters.map((meter) => ({
    node: {
      ...meter,
      rules: rulesByMeterId.get(meter.uuid) || { edges: [] },
      trades: tradesByMeterId.get(meter.uuid) || { edges: [] },
    },
  }));

  return {
    property: {
      meters: {
        edges: mergedMeterEdges,
      },
    },
  };
};

/**
 * Loads all trade-related data for a property over a time range and aggregates it
 * into a single result.
 * Accepts optional progress and UI state callbacks to report loading and
 * loaded counts for meters, trade rules and trades.
 * Returns property data shaped like the GraphQL response plus metadata about
 * the requested time window and item counts.
 * @param {object} args - Arguments for loading property trade data.
 * @param {string} args.propertyId - ID of the property to load meters, trade
 * rules and trades for.
 * @param {DateTime} args.start - Start of the time span (Luxon DateTime).
 * @param {DateTime} args.finish - End of the time span (Luxon DateTime).
 * @param {object} args.environment - Relay environment used for trade-related
 * GraphQL queries.
 * @param {number} args.pageSize - Page size used when paginating trades.
 * @param {Function} args.onStageComplete - Callback invoked with (stepIndex, totalSteps, message)
 * when a stage is completed.
 * @param {Function} args.stateDispatchFunc - Function to dispatch state changes
 * @returns {Promise<object>} Promise resolving to merged property trade data
 * with meters, rules, trades and a `_batchInfo` metadata object.
 */
export const getPropertyTradeData = async (args) => {
  const {
    propertyId,
    start,
    finish,
    environment,
    pageSize,
    onStageComplete,
    stateDispatchFunc,
  } = args;

  try {
    // Stage 1: Fetch meters
    const meters = await getPropertyMeters({ propertyId, environment });

    if (onStageComplete) {
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: TradeListLoadingActions.METERS_LOADED,
          addedMetersCount: meters.length,
          complete: true,
        });
      }

      onStageComplete(1, 3, `Loaded ${meters.length} meters`);
    }

    if (meters.length === 0) {
      logger.log('No meters found, returning empty data');
      // Dispatch remaining stage completions so the state machine progresses
      // through to RENDERING_DATA and the overlay eventually dismisses.
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: TradeListLoadingActions.RULES_LOADED,
          addedRulesCount: 0,
          complete: true,
        });
        stateDispatchFunc({
          type: TradeListLoadingActions.TRADES_LOADED,
          addedTradesCount: 0,
          complete: true,
        });
        stateDispatchFunc({ type: TradeListLoadingActions.DATA_PREPARED });
      }
      return buildPropertyTradedData([], [], []);
    }

    // Stage 2 + 3: Fetch ALL rules and ALL trades for each meter in parallel.
    // Each stage dispatches its own "complete" action as soon as it finishes so the
    // overlay can reflect partial completion (mirrors PropertyShow parallel stage UX).
    const ruleDataPromises = meters.map((meter) => getAllRulesForMeter({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      stateDispatchFunc,
    }));

    const tradeDataPromises = meters.map((meter) => getAllTradesForMeter({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      pageSize,
      stateDispatchFunc,
    }));

    let rulesCount = 0;
    let tradesCount = 0;

    const ruleDataPromise = Promise.all(ruleDataPromises).then((ruleData) => {
      rulesCount = ruleData.reduce((sum, m) => sum + (m.rules?.edges?.length || 0), 0);

      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: TradeListLoadingActions.RULES_LOADED,
          addedRulesCount: 0,
          complete: true,
        });
      }
      if (onStageComplete) {
        onStageComplete(2, 3, `Loaded ${rulesCount} rules`);
      }
      return ruleData;
    });

    const tradeDataPromise = Promise.all(tradeDataPromises).then((tradeData) => {
      tradesCount = tradeData.reduce((sum, m) => sum + (m.trades?.edges?.length || 0), 0);

      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: TradeListLoadingActions.TRADES_LOADED,
          addedTradesCount: 0,
          complete: true,
        });
      }
      if (onStageComplete) {
        onStageComplete(3, 3, `Loaded ${tradesCount} trades`);
      }
      return tradeData;
    });

    const [ruleData, tradeData] = await Promise.all([ruleDataPromise, tradeDataPromise]);

    // Merge all data
    const mergedData = buildPropertyTradedData(meters, ruleData, tradeData);

    // Dispatch DATA_PREPARED action to transition from PREPARING_DATA to RENDERING_DATA
    // This indicates that all data has been fetched and merged, and is ready for rendering
    if (stateDispatchFunc) {
      stateDispatchFunc({ type: TradeListLoadingActions.DATA_PREPARED });
    }

    // Add batch info for debugging
    return {
      ...mergedData,
      _batchInfo: {
        start: start.toISO(),
        finish: finish.toISO(),
        metersCount: meters.length,
        rulesCount,
        tradesCount,
      },
    };
  } catch (error) {
    logger.error('Error in three-stage batch processing:', error);

    // Re-throw the error to trigger onError callback in fetchPropertyTrades
    // The onError callback will handle user notification via FlashesStore
    // with more contextual error messages
    throw error;
  }
};

/**
 * Primary function responsible for fetching a property's trade data.
 * Currently makes use of a three stage process – meters, then [rules, trades].
 * Note that the stage second set can be undertaken in parallel.
 * @param {object} args - for the data batching
 * @param {string} args.propertyId - ID of the property to fetch
 * @param {DateTime} args.start - Start of the date span (Luxon DateTime)
 * @param {DateTime} args.finish - Finish of the date span (Luxon DateTime)
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {object} args.isFetchingRef - Ref to track if fetching is in progress
 * @param {number} args.pageSize - Number of trades per page
 * @param {Function} args.stateDispatchFunc - Function to dispatch state changes
 * @returns {Promise<void>} Promise that resolves when all data is fetched and processed
 */
export const fetchPropertyTrades = async (args) => {
  const {
    environment,
    finish,
    isFetchingRef,
    onDataReceived,
    onError,
    pageSize,
    propertyId,
    start,
    stateDispatchFunc,
  } = args;

  // Safety check for required parameters
  if (!propertyId || !environment || !start || !finish || !pageSize) {
    const missingArgs = [];
    if (!propertyId) missingArgs.push('propertyId');
    if (!environment) missingArgs.push('environment');
    if (!start) missingArgs.push('start');
    if (!finish) missingArgs.push('finish');
    if (!pageSize) missingArgs.push('pageSize');

    if (onError) {
      onError(new Error(`Missing required arguments: ${missingArgs.join(', ')}`));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping data request - another request is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    if (stateDispatchFunc) {
      stateDispatchFunc({ type: TradeListLoadingActions.INITIATE });
    }

    const propertyTradeResult = await getPropertyTradeData({
      propertyId,
      start,
      finish,
      environment,
      pageSize,
      onStageComplete: (stage, totalStages, message) => {
        logger.log(`Stage ${stage}/${totalStages}: ${message}`);
      },
      stateDispatchFunc,
    });

    // Wrap the object batch result in an array for filtering below
    const propertyTradeResults = [propertyTradeResult];

    // Filter out null/invalid results
    const validResults = propertyTradeResults.filter((result) => result?.property);

    if (validResults.length > 0) {
      try {
        if (validResults[0]?.property) {
          logger.log('Successfully merged three-stage trade data');

          // Notify of data received
          if (onDataReceived) {
            onDataReceived(validResults[0], validResults);
          }
        } else {
          throw new Error('Merged three-stage trade data is incomplete or invalid');
        }
      } catch (mergeError) {
        logger.error('Error merging three-stage trade data batches:', mergeError);
        if (onError) {
          onError(mergeError, 'Error processing trade data. Try a smaller date range.');
        }
      }
    } else {
      logger.warn('No valid three-stage batch results to merge');
      if (onError) {
        onError(new Error('No valid data batches received'), 'No trade data found for the selected date range.');
      }
    }
  } catch (error) {
    logger.error('Error in three-stage trade data fetching:', error);
    if (onError) {
      onError(error, 'Error fetching trade data. Please try again.');
    }
  } finally {
    // Clear fetching flag
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};
